import React, { useState } from 'react';

import { TFunction, useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Stack } from '@lyearn/ui';
import Tabs from '@/components/Tabs';
import useNavigation from '@/hooks/useNavigation';
import useSearchParam from '@/hooks/useSearchParam';
import { useEmbeddedSurveyEditor } from '@/modules/corporate/modules/course/components/SurveyCardContainer/components/SurveyEditorModal/context';
import routes from '@/pages/corporate/routes';
import { UpdateSurveyInput } from '@/types/schema';

import SurveyEditorSidebarContent from './components/SurveyEditorSidebarContent';
import { surveyEditorSidebarStyles } from './styles';
import { SurveyEditorSidebarTabEnum } from './types';

const getTabs = (t: TFunction) => [
  { label: t('SurveyEditor:Blocks'), value: SurveyEditorSidebarTabEnum.FieldSettings },
  { label: t('Common:Actions.Settings'), value: SurveyEditorSidebarTabEnum.SurveySettings },
];

export const TAB_QUERY_KEY = 'tab';

interface Props {
  survey: UpdateSurveyInput;
}

function SurveyEditorSidebar({ survey }: Props) {
  const { t } = useTranslation();
  const navigation = useNavigation();
  const embeddedEditor = useEmbeddedSurveyEditor();
  const [embeddedTab, setEmbeddedTab] = useState(SurveyEditorSidebarTabEnum.FieldSettings);
  const searchParamTab = useSearchParam(TAB_QUERY_KEY);

  const activeTab = embeddedEditor
    ? embeddedTab
    : ((searchParamTab || SurveyEditorSidebarTabEnum.FieldSettings) as SurveyEditorSidebarTabEnum);

  const setActiveTab = React.useCallback(
    (val: string) => {
      if (embeddedEditor) {
        setEmbeddedTab(
          (val || SurveyEditorSidebarTabEnum.FieldSettings) as SurveyEditorSidebarTabEnum,
        );
        return;
      }
      navigation.replace(routes.SurveyEditor(survey.id), {
        query: val ? { [TAB_QUERY_KEY]: val } : {},
      });
    },
    [embeddedEditor, navigation, survey.id],
  );

  return (
    <Stack
      className={cn(
        'flex-shrink-0 overflow-hidden overflow-y-auto border-solid border-divider pb-60 border-s',
        surveyEditorSidebarStyles,
      )}
      direction="col">
      <Tabs
        className="mt-16 border-b border-solid border-divider px-24"
        tabs={getTabs(t)}
        value={activeTab}
        variant="small"
        onChange={setActiveTab}
      />
      <SurveyEditorSidebarContent activeTab={activeTab} />
    </Stack>
  );
}

export default SurveyEditorSidebar;
